/// <reference types="@angular/localize" />
import { APP_INITIALIZER, enableProdMode, importProvidersFrom, InjectionToken, LOCALE_ID } from '@angular/core';
import { bootstrapApplication, BrowserModule } from '@angular/platform-browser';
import {
  PreloadAllModules,
  provideRouter,
  RouteReuseStrategy,
  withComponentInputBinding,
  withPreloading,
} from '@angular/router';
import { IonicRouteStrategy, provideIonicAngular } from '@ionic/angular/standalone';
import { AppComponent } from './app/app.component';
import { HTTP_INTERCEPTORS, HttpClientModule, provideHttpClient, withInterceptors } from '@angular/common/http';
import { DatePipe, registerLocaleData } from '@angular/common';
import { IonicStorageModule } from '@ionic/storage-angular';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { createTranslateLoader } from './app/shared/utils/custom-translate-loader';
import { environment } from './environments/environment';
import { AuthInterceptor } from './app/shared/interceptors/auth.interceptor';
import { routes } from './app/app-routes.module';
import localeFr from '@angular/common/locales/fr';
import { IonicModule } from '@ionic/angular';
import { MatNativeDateModule } from '@angular/material/core';
import { Storage } from '@ionic/storage';
import { provideCharts, withDefaultRegisterables } from 'ng2-charts';
import { AngularFireModule } from '@angular/fire/compat';
import { AngularFirestoreModule } from '@angular/fire/compat/firestore';
import { OidcSecurityService } from './app/oidc/oidc-security.service';
import { AdfsOidcSecurityService } from './app/oidc/adfs-oidc-security.service';
import { MockOidcSecurityService } from './app/oidc/mock-oidc-security.service';
import { NgxImageCompressService } from 'ngx-image-compress';
import { datadogRum } from '@datadog/browser-rum';
import { provideEnvironmentNgxMask } from 'ngx-mask';
import { datadogLogs } from '@datadog/browser-logs';
import { ApiModule } from './app/api/api.module';
import { provideMarkdown } from 'ngx-markdown';
import { OfflineFirstInterceptor } from './app/shared/interceptors/offline-first.interceptor';
import { RefreshOfflineFirstInterceptor } from './app/shared/interceptors/refresh-offline-first.interceptor';
import { MagasinControllerApi } from './app/api/services/magasin-controller-api';
import { MockMagasinControllerApi } from './app/gestion-stocks/providers/mock-magasin-controller-api';

if (environment.production) {
  enableProdMode();
}

datadogLogs.init({
  clientToken: environment.datadog.datadogLogsClientToken,
  service: 'dactech-front',
  env: environment.datadog.logEnvironment,
  site: environment.datadog.datadogLogsSite,
  forwardErrorsToLogs: true,
  sessionSampleRate: environment.datadog.datadogLogsSessionSampleRate,
  version: environment.appVersion,
});
datadogLogs.logger.setHandler('console'); // Handler set to console until we confirm that the toggle feature is activated

if (environment.datadog.rumEnabled) {
  datadogRum.init({
    applicationId: '91de065c-c29e-465c-aca2-a896d6f70e87',
    clientToken: 'pub867b6da2cc275ab88edb74988e65ad45',
    // `site` refers to the Datadog site parameter of your organization
    // see https://docs.datadoghq.com/getting_started/site/
    site: 'datadoghq.eu',
    service: 'd@ctech-front',
    env: environment.name,
    version: environment.appVersion,
    sessionSampleRate: environment.datadog.sessionSampleRate,
    sessionReplaySampleRate: environment.datadog.sessionReplaySampleRate,
    trackUserInteractions: environment.datadog.trackUserInteractions,
    trackResources: environment.datadog.trackResources,
    trackLongTasks: environment.datadog.trackLongTasks,
    defaultPrivacyLevel: environment.datadog.defaultPrivacyLevel,
  });
}

/**
 * This function create the indexedDB if needed and initilialize the sessionStorage with
 * the authentification credentials found in sessionStorage.
 */
function initializeDb(storage: Storage) {
  return async () => {
    return await storage.create();
  };
}

export const OIDC_SERVICE_INJECTION_TOKEN = new InjectionToken<OidcSecurityService>('OidcSecurityService');
let authent: any = AdfsOidcSecurityService;
if (environment.mockAuthentification) {
  authent = MockOidcSecurityService;
}

registerLocaleData(localeFr, 'fr');
bootstrapApplication(AppComponent, {
  providers: [
    NgxImageCompressService,
    { provide: OIDC_SERVICE_INJECTION_TOKEN, useClass: authent },
    DatePipe,
    { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: RefreshOfflineFirstInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: OfflineFirstInterceptor, multi: true },
    { provide: RouteReuseStrategy, useClass: IonicRouteStrategy },
    { provide: LOCALE_ID, useValue: 'fr-FR' },
    { provide: MagasinControllerApi, useClass: MockMagasinControllerApi },
    provideIonicAngular(),
    provideRouter(routes, withPreloading(PreloadAllModules), withComponentInputBinding()),
    provideHttpClient(),
    {
      provide: APP_INITIALIZER,
      useFactory: initializeDb,
      multi: true,
      deps: [Storage],
    },
    importProvidersFrom(
      IonicModule.forRoot({
        innerHTMLTemplatesEnabled: true,
      }),
      IonicModule,
      BrowserModule,
      HttpClientModule,
      MatNativeDateModule,
      IonicStorageModule.forRoot(),
      TranslateModule.forRoot({
        defaultLanguage: 'fr',
        useDefaultLang: true,
        loader: {
          provide: TranslateLoader,
          useFactory: createTranslateLoader,
          deps: [],
        },
      }),
      ApiModule.forRoot({ rootUrl: environment.apis.API_DACTECH_BACK }),
      AngularFireModule.initializeApp(environment.firebase),
      AngularFirestoreModule // for firestore
    ),
    provideCharts(withDefaultRegisterables()),
    provideEnvironmentNgxMask(),
    provideMarkdown(),
  ],
});
